import React, { useEffect, useState, useMemo } from "react";
import { Toaster, useId, useToastController, Toast, ToastTitle } from "@fluentui/react-components";
import {
    Select,
    MenuItem,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    SelectChangeEvent,
    Box,
    useMediaQuery,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Typography,
    Divider,
    Chip,
    Autocomplete,
    Backdrop
} from "@mui/material";
import { DefaultButton } from "@fluentui/react";
import CloseIcon from "@mui/icons-material/Close";
import { deleteCorpDataApi, deleteUserDataApi, loadUserDataApi, saveMasterSettings, saveUserDataApi } from "../../api/api";
import { appInfo } from "../../authConfig";
import i18next from "i18next";
import "../chat/Filters.css";
import Fuse from "fuse.js";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

interface DeleteDialogProps {
    open: boolean;
    onClose: (event?: any, reason?: string) => void;
    corps: Corp[];
    onLoadUsers: (corpId: string) => Promise<User[]>;
    onDeleteUser: (corpId: string, userId: string[]) => Promise<void>;
    onDeleteCorporation: (corpIds: string[]) => Promise<void>;
}

type Mode = "deleteUser" | "deleteCorp";

export interface Corp {
    corpId: string;
    corpName: string;
}

export interface User {
    userId: string;
    userName: string;
}

interface UserData {
    id: string;
    role: string;
    isWap: boolean;
    user_id: string;
    corp_id: string;
    corp_name: string;
    customer_corp_ids?: string[];
    products?: string[] | null;
    isInchargeOfAiWorks: boolean;
    isSidebarOpen: boolean;
    terms_accepted: boolean;
    isEdited?: boolean;
}

interface CustomDialogProps {
    open: boolean;
    title: string;
    message: string;
    onClose: () => void;
    onConfirm: () => void;
}

interface CustomButtonProps {
    isClear: boolean;
    text: string;
    action: () => void;
}

const MasterSettings: React.FC = () => {
    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);
    const [userData, setUserData] = useState<UserData[]>([]);
    const [corpList, setCorpList] = useState<{ key: string; text: string }[]>([]);
    const [corpUsersList, setCorpUsersList] = useState<{ key: string; value: { userId: string; userName: string }[] }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const [userDetails, setUserDetails] = useState<string>("");
    const [originalUserData, setOriginalUserData] = useState<UserData[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState(false);
    const [editedRows, setEditedRows] = useState<Record<string, any>>({});
    const [filters, setFilters] = useState<{ [key: string]: string[] }>({
        user_id: [],
        corp_name: [],
        products: [],
        corp_id: [],
        role: []
    });

    const createDropdownOptions = (data: any) => Object.keys(data).map((key: string) => data[key]);
    const roleTypeOptions = createDropdownOptions(appInfo?.role_categories);
    const [dropdownOptions, setDropdownOptions] = useState<{ [key: string]: string[] }>({
        user_id: [],
        corp_id: [],
        corp_name: [],
        products: [],
        role: []
    });
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    const cellStyles = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    };

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
    };

    useEffect(() => {
        loadTableData();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        setUserData(applyTextFieldFilter(originalUserData));
    }, [searchTerm, filters]);

    useEffect(() => {
        const isEdited = Object.values(editedRows).length === 0;
        if (isEdited) return;

        function handleOnBeforeUnload(event: BeforeUnloadEvent) {
            event.preventDefault();
            event.returnValue = "";
        }

        window.addEventListener("beforeunload", handleOnBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleOnBeforeUnload);
        };
    }, [userData]);

    const showToast = (message: string, intent: "success" | "error" | "warning" | "info"): void => {
        const intentStyles = {
            success: { color: "#28a745" },
            error: { color: "#dc3545" },
            warning: { color: "#ffc107" },
            info: { color: "#17a2b8" }
        };
        dispatchToast(
            <Toast style={{ backgroundColor: "white", borderRadius: "10px", ...intentStyles[intent] }}>
                <ToastTitle style={{ color: "black", fontWeight: "bold" }}>{message}</ToastTitle>
            </Toast>,
            { intent: intent }
        );
    };

    const loadTableData = async (): Promise<void> => {
        try {
            setIsLoading(true);
            const response = await loadUserDataApi();
            const data: UserData[] = await response.json();
            setUserData(applyTextFieldFilter(data));
            setOriginalUserData(data);
            setCorpList(getUniqueCorpIds(data));
            const uniqueValues: { [key: string]: Set<string> } = {
                user_id: new Set(),
                corp_id: new Set(),
                corp_name: new Set(),
                products: new Set()
            };
            data.forEach(item => {
                uniqueValues.user_id.add(item.user_id);
                uniqueValues.corp_id.add(item.corp_id);
                uniqueValues.corp_name.add(item.corp_name);
                item.products?.forEach(p => uniqueValues.products.add(p));
            });
            setDropdownOptions({
                user_id: Array.from(uniqueValues.user_id),
                corp_id: Array.from(uniqueValues.corp_id),
                corp_name: Array.from(uniqueValues.corp_name),
                products: Array.from(uniqueValues.products),
                role: roleTypeOptions
            });
            setEditedRows([]);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getUniqueCorpIds = (items: UserData[]) => {
        const corpIdSet = new Set<string>();
        const result: { key: string; text: string }[] = [];
        const corpBasedUsers = new Map<string, { userId: string; userName: string }[]>();
        for (const item of items) {
            if (!corpIdSet.has(item.corp_id)) {
                corpIdSet.add(item.corp_id);
                result.push({
                    key: item.corp_id,
                    text: `[${item.corp_id}] ${item.corp_name}`
                });
            }
            if (!corpBasedUsers.has(item.corp_id)) {
                corpBasedUsers.set(item.corp_id, []);
            }
            corpBasedUsers.get(item.corp_id)!.push({
                userId: item.id,
                userName: item.user_id
            });
        }
        const corpUsersArray: { key: string; value: { userId: string; userName: string }[] }[] = [];
        corpBasedUsers.forEach((users, corpId) => {
            corpUsersArray.push({ key: corpId, value: users });
        });
        setCorpUsersList(corpUsersArray);
        return result;
    };

    const handleTableSave = async (): Promise<void> => {
        try {
            setIsLoading(true);
            const editedRecords = Object.values(editedRows);
            if (editedRecords?.length === 0) {
                showToast(i18next.t("master.noChangesToSave"), "info");
                return;
            }
            const response = await saveMasterSettings(editedRecords);
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }
            setEditedRows([]);
            loadTableData();
            showToast(i18next.t("master.saveSuccess"), "success");
        } catch (error) {
            showToast(i18next.t("master.saveError"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const openUserDetailsDialog = (user: UserData) => {
        setSelectedUser(user);
        setUserDetails(JSON.stringify(user, null, 2));
        setDialogOpen(true);
    };

    const applyTextFieldFilter = (data: UserData[]) => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        setIsLoading(true);
        const filteredData = data.filter(item => {
            const { user_id, corp_name, products, corp_id, role } = item;
            const productList = Array.isArray(products) ? products : [];
            const matchesSearch = [user_id, corp_name, products?.toString(), corp_id, role].some(value => value?.toLowerCase().includes(lowerSearchTerm));
            return (
                matchesSearch &&
                (!filters.user_id.length || filters.user_id.includes(user_id)) &&
                (!filters.corp_name.length || filters.corp_name.includes(corp_name)) &&
                (!filters.products.length || filters.products.some(filterVal => productList.includes(filterVal))) &&
                (!filters.corp_id.length || filters.corp_id.includes(corp_id)) &&
                (!filters.role.length || filters.role.includes(role))
            );
        });
        setIsLoading(false);
        return filteredData;
    };

    const handleDialogSave = async () => {
        if (!selectedUser || JSON.stringify(selectedUser, null, 2) === userDetails) {
            showToast(i18next.t("master.noDataToUpdate"), "error");
            return;
        }
        setIsLoading(true);
        try {
            const response = await saveUserDataApi(userDetails);
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            } else {
                setDialogOpen(false);
                setSelectedUser(null);
                setUserDetails("");
                loadTableData();
                showToast(i18next.t("master.userDataSaved"), "success");
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Failed to save user details:", error);
        }
    };

    const handleFilterChange = (event: SelectChangeEvent<string[]>, field: string) => {
        const value = event.target.value as string[];
        if (value.includes("")) {
            setFilters(prevFilters => ({
                ...prevFilters,
                [field]: []
            }));
        } else {
            setFilters(prevFilters => ({
                ...prevFilters,
                [field]: value
            }));
        }
    };

    const handleRoleChange = (id: string, newRole: string) => {
        setUserData(prev =>
            prev.map(row => {
                if (row.id !== id) return row;
                const updatedRow = {
                    ...row,
                    role: newRole,
                    customer_corp_ids: newRole === "Consultant" ? row.customer_corp_ids || [] : []
                };
                if (JSON.stringify(updatedRow) !== JSON.stringify(row)) {
                    setEditedRows(prevEdited => ({
                        ...prevEdited,
                        [id]: updatedRow
                    }));
                }
                return updatedRow;
            })
        );
    };

    const handleCorpIdsChange = (id: string, newCorpIds: string[]) => {
        setUserData(prev =>
            prev.map(row => {
                if (row.id !== id) return row;
                const updatedRow = { ...row, customer_corp_ids: newCorpIds };
                if (JSON.stringify(updatedRow) !== JSON.stringify(row)) {
                    setEditedRows(prevEdited => ({
                        ...prevEdited,
                        [id]: updatedRow
                    }));
                }
                return updatedRow;
            })
        );
    };

    const Spinner = () => {
        return (
            <div className="full-page-spinner">
                <div className="spinner"></div>
            </div>
        );
    };

    const CustomButton = ({ isClear, text, action }: CustomButtonProps) => {
        const hasSearchOrFilters = searchTerm !== "" || Object.values(filters).some(val => val.length > 0);
        const isUserDataEdited = Object.values(editedRows)?.length !== 0;
        const enabled = isClear ? hasSearchOrFilters : isUserDataEdited;
        const opacity = enabled ? 1 : 0.5;

        return (
            <DefaultButton
                disabled={!enabled}
                style={{
                    borderRadius: 10,
                    border: "1px solid",
                    backgroundColor: "rgb(0, 120, 212)",
                    color: "white",
                    cursor: "pointer",
                    height: 40,
                    opacity
                }}
                onClick={action}
            >
                {i18next.t(text)}
            </DefaultButton>
        );
    };

    const CustomDropDown = (field: string) => {
        const dict = field?.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "60px",
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    boxSizing: "border-box"
                }}
            >
                <Box sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>{i18next.t(`master.${dict}`)}</Box>
                <Select
                    multiple
                    size="small"
                    fullWidth
                    value={filters[field] || []}
                    onChange={e => handleFilterChange(e, field)}
                    sx={{
                        fontSize: "12px",
                        minWidth: "60px",
                        maxHeight: "25px",
                        "& .MuiSelect-select": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            padding: "4px 8px"
                        }
                    }}
                    title={filters[field]?.join(",")}
                    renderValue={selected => (Array.isArray(selected) ? selected.join(", ") : selected)}
                >
                    <MenuItem value="">{i18next.t("master.ALL")}</MenuItem>
                    {dropdownOptions[field].map(field_id => (
                        <MenuItem key={field_id} value={field_id}>
                            {field_id}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    };

    const CustomDialog: React.FC<CustomDialogProps> = ({ open, title, message, onClose, onConfirm }) => {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    <span>{i18next.t(title)}</span>
                    <Button onClick={onClose} sx={{ minWidth: 0, padding: 0 }}>
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {i18next.t(message)}
                </DialogContent>
                <DialogActions sx={{ padding: "12px 24px" }}>
                    <Button onClick={onClose} variant="outlined">
                        {i18next.t("master.cancel")}
                    </Button>
                    <Button onClick={onConfirm} variant="contained" color="primary">
                        {i18next.t("master.proceed")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const CustomCellComponent = () => (params: GridRenderCellParams<any>) => (
        <div
            title={params.value}
            style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%"
            }}
        >
            {params.value}
        </div>
    );

    const columnOptions: GridColDef[] = [
        ...["user_id", "corp_id", "corp_name", "products", "role"].map(field => ({
            field,
            headerName: i18next.t(`master.${field}`),
            flex: field === "products" ? 1.5 : 1,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell:
                field === "products"
                    ? (params: GridRenderCellParams<any>) => (
                          <div title={(params.value || []).join(", ")} style={{ width: "100%" }}>
                              {(params.value || []).map((product: string, i: number) => (
                                  <div key={i} style={cellStyles}>
                                      {product}
                                  </div>
                              ))}
                          </div>
                      )
                    : field === "role"
                      ? (params: any) => (
                            <Select
                                value={params.row.role || ""}
                                title={params.row.role || ""}
                                onChange={e => handleRoleChange(params.id as string, e.target.value)}
                                fullWidth
                                size="small"
                            >
                                {roleTypeOptions.map(role => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        )
                      : CustomCellComponent(),
            renderHeader: () => <>{CustomDropDown(field)}</>
        })),
        ...["customer_corp_ids", "options"].map(field => ({
            field,
            headerName: i18next.t(`master.${field}`),
            flex: field === "customer_corp_ids" ? 2 : 1,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell:
                field === "customer_corp_ids"
                    ? (params: any) =>
                          params.row.role === "Consultant" ? (
                              <Select
                                  multiple
                                  size="small"
                                  title={params.row.customer_corp_ids || []}
                                  value={params.row.customer_corp_ids || []}
                                  onChange={e => handleCorpIdsChange(params.id as string, e.target.value as string[])}
                                  renderValue={selected => (selected as string[]).join(", ")}
                                  fullWidth
                              >
                                  {corpList.map(corp => (
                                      <MenuItem key={corp.key} value={corp.key}>
                                          {corp.text}
                                      </MenuItem>
                                  ))}
                              </Select>
                          ) : null
                    : (params: GridRenderCellParams<any>) => (
                          <Button variant="outlined" onClick={() => openUserDetailsDialog(params.row)}>
                              {i18next.t("master.viewMore")}
                          </Button>
                      )
        }))
    ];

    const fetchUsersByCorp = async (corpId: string): Promise<User[]> => {
        if (!corpId) return [];
        return corpUsersList.find(c => c.key === corpId)?.value || [];
    };

    const apiDeleteUser = async (corpId: string, userIds: string[]): Promise<void> => {
        try {
            setIsLoading(true);
            await deleteUserDataApi(corpId, userIds);
            loadTableData();
        } catch (error) {
            console.error("Error in deleting data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    async function apiDeleteCorporation(corpIds: string[]): Promise<void> {
        try {
            setIsLoading(true);
            await deleteCorpDataApi(corpIds);
            loadTableData();
        } catch (error) {
            console.error("Error in deleting data:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const DeleteDialog: React.FC<DeleteDialogProps> = ({ open, onClose, corps, onLoadUsers, onDeleteUser, onDeleteCorporation }) => {
        const [mode, setMode] = useState<Mode>("deleteUser");
        const [users, setUsers] = useState<User[]>([]);
        const [selectedCorpIds, setSelectedCorpIds] = useState<string[]>([]);
        const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
        const [loadingUsers, setLoadingUsers] = useState(false);
        const [submitting, setSubmitting] = useState(false);
        const [confirmOpen, setConfirmOpen] = useState(false);

        const corpFuse = useMemo(
            () =>
                new Fuse(corps, {
                    keys: ["corpId", "corpName"],
                    threshold: 0.4
                }),
            [corps]
        );

        const userFuse = useMemo(
            () =>
                new Fuse(users, {
                    keys: ["userId", "userName"],
                    threshold: 0.4
                }),
            [users]
        );

        useEffect(() => {
            if (!open) return;
            setMode("deleteUser");
            setUsers([]);
            setSelectedCorpIds([]);
            setSelectedUserIds([]);
            setLoadingUsers(false);
            setSubmitting(false);
            setConfirmOpen(false);
        }, [open]);

        const loadUsersForCorp = async (corpId: string) => {
            if (!corpId) {
                setUsers([]);
                return;
            }
            setLoadingUsers(true);
            try {
                const data = await onLoadUsers(corpId);
                setUsers(data);
            } finally {
                setLoadingUsers(false);
            }
        };

        const loadUsersForCorps = async (corpIds: string[]) => {
            if (corpIds.length === 0) {
                setUsers([]);
                setSelectedUserIds([]);
                return;
            }
            setLoadingUsers(true);
            try {
                const allUsersMap = new Map<string, User>();
                for (const id of corpIds) {
                    const data = await onLoadUsers(id);
                    data.forEach(u => allUsersMap.set(u.userId, u));
                }
                const mergedUsers = Array.from(allUsersMap.values());
                setUsers(mergedUsers);
                setSelectedUserIds(mergedUsers.map(u => u.userId));
            } finally {
                setLoadingUsers(false);
            }
        };

        const handleCorpChange = async (_event: any, newValue: Corp[] | Corp | null) => {
            if (mode === "deleteCorp") {
                const list = (newValue as Corp[] | null) || [];
                const ids = list.map(c => c.corpId);
                setSelectedCorpIds(ids);
                setUsers([]);
                setSelectedUserIds([]);
                await loadUsersForCorps(ids);
            } else {
                const corp = newValue as Corp | null;
                const id = corp?.corpId || "";
                setSelectedCorpIds(id ? [id] : []);
                setSelectedUserIds([]);
                setUsers([]);
                if (id) {
                    await loadUsersForCorp(id);
                }
            }
        };

        const handleProceedClick = () => {
            setConfirmOpen(true);
        };

        const handleConfirm = async () => {
            setConfirmOpen(false);
            setSubmitting(true);
            onClose();
            try {
                const singleCorpId = selectedCorpIds[0] || "";
                if (mode === "deleteUser") {
                    await onDeleteUser(singleCorpId, selectedUserIds);
                } else {
                    await onDeleteCorporation(selectedCorpIds);
                }
                showToast(i18next.t("master.deleteSuccess"), "warning");
            } catch (err) {
                showToast(i18next.t("master.deleteFailed"), "error");
                console.error(err);
            } finally {
                setSubmitting(false);
            }
        };

        const corpFilterOptions = (options: Corp[], params: any) => {
            const query = params.inputValue?.trim();
            if (!query) return options;
            return corpFuse.search(query).map(r => r.item);
        };
        const userFilterOptions = (options: User[], params: any) => {
            const query = params.inputValue?.trim();
            if (!query) return options;
            return userFuse.search(query).map(r => r.item);
        };

        const selectedCorpDisplayNames = selectedCorpIds.map(id => {
            const corp = corps.find(c => c.corpId === id);
            return corp ? corp.corpName : id;
        });

        const selectedUserDisplayNames = selectedUserIds.map(id => {
            const user = users.find(u => u.userId === id);
            return user ? user.userName : id;
        });

        const singleCorpId = selectedCorpIds[0] || "";
        const selectedCorpObjectsMulti = corps.filter(c => selectedCorpIds.includes(c.corpId));
        const selectedCorpObjectSingle = corps.find(c => c.corpId === singleCorpId) || null;
        const isDeleteUserValid = !!singleCorpId && selectedUserIds.length > 0;
        const isDeleteCorpValid = selectedCorpIds.length > 0;
        const isProceedDisabled = submitting || (mode === "deleteUser" ? !isDeleteUserValid : !isDeleteCorpValid);
        const selectedUserObjects = users.filter(u => selectedUserIds.includes(u.userId));
        const isUsersReadOnly = mode === "deleteCorp";
        const isUsersFieldDisabled =
            mode === "deleteUser" ? !singleCorpId || loadingUsers || users.length === 0 : selectedCorpIds.length === 0 || loadingUsers || users.length === 0;

        return (
            <>
                <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                    <DialogTitle
                        sx={{
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}
                    >
                        <span>{i18next.t("master.deleteManagement")}</span>
                        <Button onClick={onClose} disabled={submitting} sx={{ minWidth: 0, padding: 0 }}>
                            <CloseIcon />
                        </Button>
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{
                            padding: "16px 24px",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary">
                            {mode === "deleteUser" ? i18next.t("master.deleteUserDescription") : i18next.t("master.deleteCorpDescription")}
                        </Typography>

                        <Divider />

                        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                            <RadioGroup
                                row
                                value={mode}
                                onChange={e => {
                                    const newMode = e.target.value as Mode;
                                    setMode(newMode);
                                    setSelectedCorpIds([]);
                                    setSelectedUserIds([]);
                                    setUsers([]);
                                }}
                            >
                                <FormControlLabel value="deleteUser" control={<Radio size="small" />} label={i18next.t("master.deleteUser")} />
                                <FormControlLabel value="deleteCorp" control={<Radio size="small" />} label={i18next.t("master.deleteCorporation")} />
                            </RadioGroup>
                        </Box>

                        <Autocomplete
                            multiple={mode === "deleteCorp"}
                            options={corps}
                            filterOptions={corpFilterOptions}
                            getOptionLabel={option => `${option.corpName}`}
                            value={mode === "deleteCorp" ? selectedCorpObjectsMulti : selectedCorpObjectSingle}
                            onChange={(event, newValue) => {
                                if (mode === "deleteCorp") {
                                    handleCorpChange(event, newValue as Corp[]);
                                } else {
                                    handleCorpChange(event, (newValue as Corp | null) || null);
                                }
                            }}
                            renderTags={(value: readonly Corp[], getTagProps) => (
                                <Box
                                    sx={{
                                        overflowY: "auto",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5
                                    }}
                                >
                                    {value.map((option: Corp, index: number) => (
                                        <Chip label={option.corpName} size="small" {...getTagProps({ index })} />
                                    ))}
                                </Box>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label={i18next.t("master.corpId")}
                                    placeholder={mode === "deleteCorp" ? i18next.t("master.searchCorp") : i18next.t("master.selectCorp")}
                                />
                            )}
                        />

                        <Autocomplete
                            disabled={!isUsersReadOnly && isUsersFieldDisabled}
                            multiple={true}
                            options={users}
                            filterOptions={userFilterOptions}
                            getOptionLabel={option => `${option.userName}`}
                            value={selectedUserObjects}
                            onChange={(e, val) => {
                                if (mode === "deleteUser") {
                                    setSelectedUserIds((val as User[]).map(u => u.userId));
                                }
                            }}
                            renderTags={(value: readonly User[], getTagProps) => (
                                <Box
                                    sx={{
                                        overflowY: "auto",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5
                                    }}
                                >
                                    {value.map((option: User, index: number) => (
                                        <Chip label={`${option.userName}`} size="small" {...getTagProps({ index })} />
                                    ))}
                                </Box>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label={i18next.t("master.usersInCorp")}
                                    placeholder={mode === "deleteUser" ? i18next.t("master.searchUsers") : ""}
                                />
                            )}
                            noOptionsText={loadingUsers ? i18next.t("master.loading") : i18next.t("master.noUsersForCorp")}
                        />

                        {mode === "deleteUser" && selectedUserIds.length > 0 && (
                            <Typography variant="caption" color="error">
                                {`${i18next.t("master.about")}${selectedUserIds.length}${i18next.t("master.aboutUsers")}`}
                            </Typography>
                        )}
                        {mode === "deleteCorp" && selectedCorpIds.length > 0 && (
                            <Typography variant="caption" color="error">
                                {`${i18next.t("master.about")}${selectedCorpIds.length}${i18next.t("master.aboutCorporations")}`}
                            </Typography>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ padding: "12px 24px" }}>
                        <Button onClick={onClose} variant="outlined" disabled={submitting}>
                            {i18next.t("master.cancel")}
                        </Button>
                        <Button onClick={handleProceedClick} variant="contained" color="error" disabled={isProceedDisabled}>
                            {i18next.t("master.update")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Backdrop open={submitting} sx={{ zIndex: theme => theme.zIndex.modal + 1 }}>
                    <Box sx={{ width: "60%", maxWidth: 600 }}>
                        <CircularProgress />
                    </Box>
                </Backdrop>

                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>{i18next.t("master.confirmDialog")}</DialogTitle>
                    <DialogContent dividers>
                        {mode === "deleteUser" ? (
                            <>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {i18next.t("master.confirmDeleteUsersFromCorp")}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {i18next.t("master.corporation")}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-line", mb: 1 }}>
                                    {selectedCorpDisplayNames[0] || "-"}
                                </Typography>

                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {i18next.t("master.users")}
                                </Typography>
                                {selectedUserDisplayNames.map((name, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {name}
                                    </Typography>
                                ))}
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {i18next.t("master.confirmDeleteCorporations")}
                                </Typography>
                                {selectedCorpDisplayNames.map((name, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {name}
                                    </Typography>
                                ))}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setConfirmOpen(false)} disabled={submitting}>
                            {i18next.t("master.cancel")}
                        </Button>
                        <Button variant="contained" color="error" onClick={handleConfirm} disabled={submitting}>
                            {i18next.t("master.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    return (
        <div>
            {isLoading && <Spinner />}
            <Toaster toasterId={toasterId} position="bottom-start" pauseOnHover pauseOnWindowBlur timeout={2000} />
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    padding: "13px",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px"
                }}
            >
                <h2
                    style={{
                        fontSize: useMediaQuery("(min-width:1400px)") ? "xxx-large" : "xx-large",
                        fontWeight: "bold",
                        fontFamily: "BIZ UDPGothic, sans-serif",
                        display: "flex",
                        flex: 1,
                        margin: 0
                    }}
                >
                    {i18next.t("master.tableHeader")}
                </h2>
                <Box sx={{ p: 0, gap: 1, display: "flex", alignItems: "center" }}>
                    <TextField
                        label={i18next.t("master.search_user_corp")}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                        }}
                        style={{
                            borderRadius: "10px",
                            border: "1px solid",
                            color: "white",
                            cursor: "pointer"
                        }}
                    />
                    <CustomButton
                        isClear={true}
                        text={"master.clearFilters"}
                        action={() => {
                            setFilters({
                                user_id: [],
                                corp_name: [],
                                products: [],
                                corp_id: [],
                                role: []
                            });
                            setSearchTerm("");
                        }}
                    />
                    <CustomButton
                        isClear={false}
                        text={"master.updateUserDetails"}
                        action={() => {
                            setSaveConfirmDialogOpen(true);
                        }}
                    />
                    <DefaultButton
                        color="error"
                        disabled={userData.length === 0}
                        style={{
                            borderRadius: 10,
                            border: "1px solid",
                            backgroundColor: "rgba(238, 85, 85, 1)",
                            color: "white",
                            cursor: "pointer",
                            height: 40,
                            opacity: userData.length === 0 ? 0.5 : 1
                        }}
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        {i18next.t("master.openDeleteDialog")}
                    </DefaultButton>
                    <DeleteDialog
                        open={deleteDialogOpen}
                        onClose={(event, reason) => {
                            if (reason === "backdropClick") return;
                            setDeleteDialogOpen(false);
                        }}
                        corps={corpList.map(corp => ({ corpId: corp.key, corpName: corp.text }))}
                        onLoadUsers={fetchUsersByCorp}
                        onDeleteUser={apiDeleteUser}
                        onDeleteCorporation={apiDeleteCorporation}
                    />
                </Box>
            </div>
            <div>
                <Paper
                    sx={{
                        background: "rgba(255, 255, 255, 0.2)",
                        backdropFilter: "blur(10px)",
                        borderRadius: "10px",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                        maxHeight: window.innerHeight - 115,
                        overflow: "hidden",
                        height: "100vh"
                    }}
                >
                    <DataGrid
                        rows={userData}
                        columns={columnOptions}
                        getRowId={row => row.id}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 50,
                                    page: 0
                                }
                            }
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        checkboxSelection={false}
                        sx={{
                            "& .MuiDataGrid-cell:hover": { backgroundColor: "#f5f5f5" },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: 1.5,
                                py: 1
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "rgba(0, 0, 0, 0.05)",
                                fontWeight: "bold",
                                height: "100%"
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontSize: "16px",
                                fontWeight: "bold"
                            }
                        }}
                        getRowHeight={() => "auto"}
                        style={{ width: "100%" }}
                        editMode="cell"
                        disableRowSelectionOnClick
                        disableColumnFilter
                        disableColumnSelector
                    />
                </Paper>
            </div>
            <CustomDialog
                open={saveConfirmDialogOpen}
                onClose={() => {
                    setSaveConfirmDialogOpen(false);
                }}
                title={i18next.t("master.saveChanges")}
                message={i18next.t("master.saveChangesMessage")}
                onConfirm={() => {
                    if (dialogOpen) {
                        handleDialogSave();
                    } else {
                        handleTableSave();
                    }
                    setSaveConfirmDialogOpen(false);
                }}
            />
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    <span>{i18next.t("master.userdetails")}</span>
                    <Button onClick={() => setDialogOpen(false)} sx={{ minWidth: 0, padding: 0 }}>
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {dialogOpen && isLoading && (
                        <div className="full-page-spinner">
                            <div className="spinner"></div>
                        </div>
                    )}
                    <TextField
                        multiline
                        fullWidth
                        defaultValue={userDetails}
                        onChange={(e: any) => {
                            setUserDetails(e.target.value);
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: "12px 24px" }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">
                        {i18next.t("master.cancel")}
                    </Button>
                    <Button
                        disabled={dialogOpen && (!selectedUser || JSON.stringify(selectedUser, null, 2) === userDetails)}
                        onClick={() => {
                            setSaveConfirmDialogOpen(true);
                        }}
                        variant="contained"
                        color="primary"
                    >
                        {i18next.t("master.proceed")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
export default MasterSettings;
